import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subscription, timer, switchMap, tap, catchError, of } from 'rxjs';
import { skipLoading } from '@core/interceptors/loading.interceptor';
import { ApiClientService } from '../api-client.service';

export interface UserNotification {
  id: string;
  title: string;
  module: string;
  code: string;
  route: string;
  dueDate: string | null;
  /** When the underlying record last changed (ISO); the API returns items newest-first by this. */
  updatedAt?: string | null;
  severity: 'warning' | 'critical';
  type: 'overdue' | 'action-required' | 'upcoming';
  message: string;
}

@Injectable({ providedIn: 'root' })
export class UserNotificationService {
  private api = inject(ApiClientService);
  private polling?: Subscription;

  readonly notifications = signal<UserNotification[]>([]);
  readonly count = signal(0);
  readonly loading = signal(false);

  /** Fetch notifications from the API */
  loadNotifications(): Observable<UserNotification[]> {
    this.loading.set(true);
    // Background poll — it must never raise the global busy overlay, which would blank the screen
    // every five minutes while the user is working.
    return this.api.get<UserNotification[]>('/notifications', skipLoading()).pipe(
      tap(items => {
        this.notifications.set(items);
        this.count.set(items.length);
        this.loading.set(false);
      }),
      catchError(() => {
        this.loading.set(false);
        return of([]);
      }),
    );
  }

  /** Start polling notifications every 5 minutes */
  startPolling(): void {
    if (this.polling && !this.polling.closed) return;
    this.polling = new Subscription();
    this.polling.add(this.loadNotifications().subscribe());
    this.polling.add(timer(300_000, 300_000).pipe(
      switchMap(() => this.loadNotifications()),
    ).subscribe());
  }

  stopPolling(): void {
    this.polling?.unsubscribe();
    this.polling = undefined;
    this.notifications.set([]);
    this.count.set(0);
    this.loading.set(false);
  }
}
